import React, { useState, useEffect, useRef, useCallback } from 'react';
import { emailAPI } from '../services/api';
import './EmailHistory.css';

const EmailHistory = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [statusFilter, setStatusFilter] = useState('');
  const [brandIdFilter, setBrandIdFilter] = useState('');
  const [jobIdFilter, setJobIdFilter] = useState('');
  const [sortColumn, setSortColumn] = useState('sentAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const tableScrollRef = useRef(null);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const currentPage = pagination?.page || 1;
      const currentLimit = pagination?.limit || 50;
      const params = {
        page: currentPage,
        limit: currentLimit,
        ...(statusFilter && { status: statusFilter }),
        ...(brandIdFilter && { brandId: brandIdFilter }),
        ...(jobIdFilter && { jobId: jobIdFilter }),
      };
      const response = await emailAPI.getHistory(params);
      setEmails(response.data?.data || []);
      setPagination(response.data?.pagination || { 
        page: currentPage, 
        limit: currentLimit, 
        total: 0, 
        totalPages: 0 
      });
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch email history');
      // Keep current pagination on error
      setPagination(prev => prev || { page: 1, limit: 50, total: 0, totalPages: 0 });
    } finally {
      setLoading(false);
    }
  }, [pagination?.page, pagination?.limit, statusFilter, brandIdFilter, jobIdFilter]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ 
      ...prev, 
      page: newPage,
      limit: prev?.limit || 50,
      total: prev?.total || 0,
      totalPages: prev?.totalPages || 0
    }));
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const scrollTable = (direction) => {
    if (tableScrollRef.current) {
      const scrollAmount = 300;
      const currentScroll = tableScrollRef.current.scrollLeft;
      const maxScroll = tableScrollRef.current.scrollWidth - tableScrollRef.current.clientWidth;
      const newPosition = direction === 'left' 
        ? Math.max(0, currentScroll - scrollAmount)
        : Math.min(maxScroll, currentScroll + scrollAmount);
      tableScrollRef.current.scrollTo({ left: newPosition, behavior: 'smooth' });
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <span className="sort-icon">⇅</span>;
    }
    return sortDirection === 'asc' ? <span className="sort-icon">↑</span> : <span className="sort-icon">↓</span>;
  };

  // Sort emails locally (since backend doesn't support sorting yet)
  const sortedEmails = [...emails].sort((a, b) => {
    let aVal = a[sortColumn];
    let bVal = b[sortColumn];
    
    if (aVal === null || aVal === undefined) aVal = '';
    if (bVal === null || bVal === undefined) bVal = '';
    
    if (sortColumn === 'sentAt') {
      aVal = new Date(aVal).getTime();
      bVal = new Date(bVal).getTime();
    } else {
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
    }
    
    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
    }
  });

  if (loading && emails.length === 0) {
    return <div className="loading">Loading email history...</div>;
  }

  return (
    <div className="email-history">
      <div className="page-header">
        <h1 className="page-title">Email History (SendGrid Logs)</h1>
        <div className="filters" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <select
            className="form-select"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPagination({ ...pagination, page: 1 });
            }}
            style={{ width: '200px' }}
          >
            <option value="">All Status</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
            <option value="delivered">Delivered</option>
            <option value="opened">Opened</option>
            <option value="clicked">Clicked</option>
            <option value="bounced">Bounced</option>
            <option value="dropped">Dropped</option>
          </select>
          <input
            type="text"
            className="form-input"
            placeholder="Filter by Brand ID"
            value={brandIdFilter}
            onChange={(e) => {
              setBrandIdFilter(e.target.value);
              setPagination({ ...pagination, page: 1 });
            }}
            style={{ width: '200px' }}
          />
          <input
            type="text"
            className="form-input"
            placeholder="Filter by Job ID"
            value={jobIdFilter}
            onChange={(e) => {
              setJobIdFilter(e.target.value);
              setPagination({ ...pagination, page: 1 });
            }}
            style={{ width: '200px' }}
          />
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {emails.length === 0 ? (
        <div className="card">
          <p>No emails found.</p>
        </div>
      ) : (
        <>
          <div className="card table-container-wrapper">
            <div className="table-scroll-controls">
              <button 
                className="scroll-arrow scroll-arrow-left"
                onClick={() => scrollTable('left')}
                title="Scroll left"
              >
                ←
              </button>
              <div className="table-container" ref={tableScrollRef}>
                <table className="table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('to')} className="sortable">
                        To {getSortIcon('to')}
                      </th>
                      <th onClick={() => handleSort('from')} className="sortable">
                        From {getSortIcon('from')}
                      </th>
                      <th onClick={() => handleSort('templateId')} className="sortable">
                        Template ID {getSortIcon('templateId')}
                      </th>
                      <th onClick={() => handleSort('brandId')} className="sortable">
                        Brand ID {getSortIcon('brandId')}
                      </th>
                      <th onClick={() => handleSort('jobId')} className="sortable">
                        Job ID {getSortIcon('jobId')}
                      </th>
                      <th onClick={() => handleSort('messageId')} className="sortable">
                        Message ID {getSortIcon('messageId')}
                      </th>
                      <th onClick={() => handleSort('status')} className="sortable">
                        Status {getSortIcon('status')}
                      </th>
                      <th onClick={() => handleSort('sentAt')} className="sortable">
                        Sent At {getSortIcon('sentAt')}
                      </th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedEmails.map((email) => (
                  <tr key={email.id || email.messageId}>
                    <td>{email.to}</td>
                    <td>
                      {email.fromName ? `${email.fromName} <${email.from}>` : email.from}
                      {email.replyTo && email.replyTo !== email.from && (
                        <div style={{ fontSize: '0.85em', color: '#666' }}>
                          Reply-To: {email.replyTo}
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: '0.85em' }}>{email.templateId || 'N/A'}</td>
                    <td>{email.brandId || 'N/A'}</td>
                    <td style={{ fontSize: '0.85em' }}>
                      {email.jobId ? (
                        <span title={email.jobId}>{email.jobId.substring(0, 20)}...</span>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td style={{ fontSize: '0.75em', fontFamily: 'monospace' }}>
                      {email.messageId || 'N/A'}
                    </td>
                    <td>
                      <span className={`badge ${
                        email.status === 'sent' || email.status === 'delivered' ? 'badge-success' :
                        email.status === 'opened' || email.status === 'clicked' ? 'badge-info' :
                        email.status === 'failed' || email.status === 'bounced' || email.status === 'dropped' ? 'badge-danger' :
                        'badge-warning'
                      }`}>
                        {email.status}
                      </span>
                    </td>
                    <td>{email.sentAt ? new Date(email.sentAt).toLocaleString() : 'N/A'}</td>
                    <td style={{ fontSize: '0.85em', color: '#d9534f', maxWidth: '200px' }}>
                      {email.error ? (
                        <span title={email.error}>{email.error.substring(0, 50)}...</span>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button 
                className="scroll-arrow scroll-arrow-right"
                onClick={() => scrollTable('right')}
                title="Scroll right"
              >
                →
              </button>
            </div>
          </div>

          {/* Pagination with Arrows */}
          {pagination && pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                className="btn btn-secondary pagination-arrow"
                onClick={() => handlePageChange((pagination.page || 1) - 1)}
                disabled={(pagination.page || 1) === 1}
                title="Previous page"
              >
                ← Previous
              </button>
              <button
                className="btn btn-secondary pagination-arrow pagination-arrow-icon"
                onClick={() => handlePageChange((pagination.page || 1) - 1)}
                disabled={(pagination.page || 1) === 1}
                title="Previous page"
              >
                ←
              </button>
              <span className="pagination-info">
                Page {pagination.page || 1} of {pagination.totalPages || 0} ({pagination.total || 0} total)
              </span>
              <button
                className="btn btn-secondary pagination-arrow pagination-arrow-icon"
                onClick={() => handlePageChange((pagination.page || 1) + 1)}
                disabled={(pagination.page || 1) >= (pagination.totalPages || 0)}
                title="Next page"
              >
                →
              </button>
              <button
                className="btn btn-secondary pagination-arrow"
                onClick={() => handlePageChange((pagination.page || 1) + 1)}
                disabled={(pagination.page || 1) >= (pagination.totalPages || 0)}
                title="Next page"
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EmailHistory;

